import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Search, X } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  "superadmin",
  "aayda_chairman",
  "aayda_tech_supervision",
  "aayda_construction",
  "aayda_finance",
  "consulting_team_lead",
  "consulting_tech_office",
  "consulting_inspector",
  "consulting_topographer",
  "consulting_material_engineer",
  "consulting_estimate_engineer",
  "contractor",
];

export default function AdminUsers() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user object being edited
  const [editForm, setEditForm] = useState({});
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [form, setForm] = useState({
    full_name: "",
    position: "",
    role: "consulting_inspector",
    email: "",
    password: "Demo123!",
    organization_type: "consulting",
    organization_name: "",
  });

  const load = () => api.get("/users").then((r) => setUsers(r.data));

  useEffect(() => {
    load();
  }, []);

  if (user?.role !== "superadmin") {
    return (
      <div className="text-slate-500">
        Yalnız Super Admin bu səhifəyə daxil ola bilər.
      </div>
    );
  }

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("İstifadəçi yaradıldı");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Sil?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      full_name: u.full_name || "",
      position: u.position || "",
      role: u.role,
      email: u.email,
      password: "",
      organization_type: u.organization_type,
      organization_name: u.organization_name || "",
    });
  };

  const submitEdit = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("Yeniləndi");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = users.filter((u) => {
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    if (!q) return true;
    return (
      (u.full_name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.organization_name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-overline mb-1">{t("users")}</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">
            İstifadəçilər
          </h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#004D99] hover:bg-[#003366]"
              data-testid="new-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> {t("create_user")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("create_user")}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="label-overline">{t("full_name")}</Label>
                <Input
                  value={form.full_name}
                  onChange={(e) =>
                    setForm({ ...form, full_name: e.target.value })
                  }
                  data-testid="user-full-name"
                />
              </div>
              <div>
                <Label className="label-overline">{t("email")}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="user-email"
                />
              </div>
              <div>
                <Label className="label-overline">{t("password")}</Label>
                <Input
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  data-testid="user-password"
                />
              </div>
              <div>
                <Label className="label-overline">{t("position")}</Label>
                <Input
                  value={form.position}
                  onChange={(e) =>
                    setForm({ ...form, position: e.target.value })
                  }
                />
              </div>
              <div>
                <Label className="label-overline">{t("role")}</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => {
                    let org = "consulting";
                    if (v.startsWith("aayda")) org = "aayda";
                    else if (v === "contractor") org = "contractor";
                    else if (v === "superadmin") org = "platform";
                    setForm({ ...form, role: v, organization_type: org });
                  }}
                >
                  <SelectTrigger data-testid="user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {t(`role_label.${r}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-overline">{t("organization")}</Label>
                <Input
                  value={form.organization_name}
                  onChange={(e) =>
                    setForm({ ...form, organization_name: e.target.value })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={create}
                className="bg-[#004D99] hover:bg-[#003366]"
                data-testid="user-create-submit"
              >
                {t("create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ad, email və ya təşkilat..."
            className="pl-9 pr-9 bg-white"
            data-testid="users-search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[240px] bg-white" data-testid="users-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Bütün rollar</SelectItem>
            {ROLES.map((r) => (
              <SelectItem key={r} value={r}>{t(`role_label.${r}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="mono text-xs text-slate-500" data-testid="users-count">
          {filtered.length} / {users.length}
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left">
              <th className="px-4 py-3 label-overline">Ad Soyad</th>
              <th className="px-4 py-3 label-overline">Email</th>
              <th className="px-4 py-3 label-overline">Rol</th>
              <th className="px-4 py-3 label-overline">Təşkilat</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold">{u.full_name}</td>
                <td className="px-4 py-3 mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 bg-slate-100 rounded">
                    {t(`role_label.${u.role}`)}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {u.organization_name}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(u)}
                    data-testid={`edit-user-${u.id}`}
                  >
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </Button>
                  {u.role !== "superadmin" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(u.id)}
                      data-testid={`del-user-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-slate-400">
                  İstifadəçi tapılmadı
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="user-edit-dialog">
          <DialogHeader>
            <DialogTitle>İstifadəçini redaktə et</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-overline">{t("full_name")}</Label>
              <Input
                value={editForm.full_name || ""}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                data-testid="edit-full-name"
              />
            </div>
            <div>
              <Label className="label-overline">{t("email")}</Label>
              <Input
                type="email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="edit-email"
              />
            </div>
            <div>
              <Label className="label-overline">Yeni şifrə (boş qoyun = dəyişməz)</Label>
              <Input
                value={editForm.password || ""}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="dəyişdirmək üçün doldurun"
                data-testid="edit-password"
              />
            </div>
            <div>
              <Label className="label-overline">{t("position")}</Label>
              <Input
                value={editForm.position || ""}
                onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
              />
            </div>
            <div>
              <Label className="label-overline">{t("role")}</Label>
              <Select
                value={editForm.role}
                onValueChange={(v) => {
                  let org = "consulting";
                  if (v.startsWith("aayda")) org = "aayda";
                  else if (v === "contractor") org = "contractor";
                  else if (v === "superadmin") org = "platform";
                  setEditForm({ ...editForm, role: v, organization_type: org });
                }}
              >
                <SelectTrigger data-testid="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {t(`role_label.${r}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="label-overline">{t("organization")}</Label>
              <Input
                value={editForm.organization_name || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, organization_name: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {t("cancel")}
            </Button>
            <Button
              onClick={submitEdit}
              className="bg-[#004D99] hover:bg-[#003366]"
              data-testid="user-edit-submit"
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
